import React, { useState, useEffect, useRef, useMemo } from "react";
import { Play, Pause, Upload, Library as LibraryIcon, Sparkles, Music4, X, Search, Trash2, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { synthPreview } from "@/lib/psAudio";
import s from "../../pages/ProductionSuite.module.css";
import SfxBrowser from "../SfxBrowser";
import { appConfirm } from "@/components/AppDialogHost";

// NOTE: Seed asset cards were removed after users reported the WebAudio
// synth previews couldn't be reliably paused on some browsers. The library
// now only shows real audio assets (imports, stems, AI-generated tracks)
// whose `audio_url` streams from our backend and pauses cleanly.
const SEED_ASSETS = [];

const FILTERS = ["All", "Uploads", "Voice", "Beds", "Jingles", "SFX", "AI gen"];
const FILTER_TO_TYPE = {
  All: null,
  Uploads: "__uploads__",   // synthetic — matched in the filter function
  Voice: "Voice",
  Beds: "Bed",
  Jingles: "Jingle",
  SFX: "SFX",
  "AI gen": "AI gen",
};

// A user-uploaded asset lives at /api/production/import/… with
// source === "imaging". SFX pack items, stems, and system imaging
// don't match, which is exactly what we want on the "Uploads" tab.
// Update #162 — also include commercial voices (generated by the AI
// Commercial Creator), which stream via /api/imaging/assets/… and carry
// a `commercial_id`.
const isUploadedAsset = (a) => {
  if (a.source !== "imaging") return false;
  const url = typeof a.audio_url === "string" ? a.audio_url : "";
  if (url.startsWith("/api/production/import/")) return true;
  if (url.startsWith("/api/imaging/assets/")) return true;
  return false;
};

export default function PSAssetLibrary({ onDragStartAsset, onOpenImport, highlightAssetId }) {
  const [filter, setFilter] = useState("All");
  const [items, setItems] = useState([]);
  const [sfxCategories, setSfxCategories] = useState([]);
  const [sfxRecentIds, setSfxRecentIds] = useState([]);
  const [category, setCategory] = useState(null);   // null = all categories
  const [showRecentOnly, setShowRecentOnly] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [sfxOpen, setSfxOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Update #156 — id of the card whose name is currently in inline-edit mode.
  const [editingId, setEditingId] = useState(null);
  // Update #162 — id of the card to pulse briefly (deep-link entry).
  const [pulseId, setPulseId] = useState(null);
  // ── Preview audio plumbing ─────────────────────────────────────────────
  // Single shared ref + cancellable auto-stop timer so clicking through
  // multiple previews stops the previous one cleanly (runaway-audio fix).
  const audioRef = useRef(null);
  const stopTimerRef = useRef(null);
  // Handle returned by `synthPreview()` for seed assets that use WebAudio
  // instead of an HTMLAudioElement. Without this, clicking Pause on a seed
  // asset would leave the oscillators playing until their scheduled natural
  // end — exactly the "audio never stops" bug the user reported.
  const synthStopRef = useRef(null);
  // Track the id we dispatched with `bfa:preview-started` so we can send the
  // matching `bfa:preview-ended` when the user clicks Pause / picks another
  // asset. Without this, the floating NowPreviewingBar pill keeps animating
  // for its full 8 s window even though the audio has already stopped — and
  // users understandably read that as "the audio didn't stop".
  const previewEventIdRef = useRef(null);
  const stopPreview = React.useCallback(() => {
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }
    const a = audioRef.current;
    if (a) {
      try { a.pause(); a.currentTime = 0; } catch { /* noop */ }
      a.onended = null;
      a.onerror = null;
      audioRef.current = null;
    }
    // Kill any in-flight WebAudio synth preview from a seed asset.
    if (synthStopRef.current) {
      try { synthStopRef.current(); } catch { /* noop */ }
      synthStopRef.current = null;
    }
    // Dismiss the app-wide "Now previewing" pill matched to our own started-event id.
    if (previewEventIdRef.current) {
      try {
        window.dispatchEvent(new CustomEvent("bfa:preview-ended", {
          detail: { id: previewEventIdRef.current },
        }));
      } catch { /* noop */ }
      previewEventIdRef.current = null;
    }
    setPreviewId(null);
  }, []);
  // Listen for the global "stop all previews" beacon Esc + Stop dispatch.
  // Also tear down on unmount so navigating away kills any audio.
  useEffect(() => {
    const onStopAll = () => stopPreview();
    window.addEventListener("bfa:ps-stop-all-preview", onStopAll);
    return () => {
      window.removeEventListener("bfa:ps-stop-all-preview", onStopAll);
      stopPreview();
    };
  }, [stopPreview]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/production/assets");
        const real = r.data?.items || [];
        // Mix real imaging/avatar assets in front, seed assets after
        setItems([...real, ...SEED_ASSETS]);
        setSfxCategories(r.data?.sfx_categories || []);
        setSfxRecentIds(r.data?.sfx_recent || []);
      } catch {
        setItems(SEED_ASSETS);
      }
    })();
  }, []);

  // Update #162 — react to `?highlight=<assetId>` deep-links from other
  // parts of the app (Commercials → Production Suite handoff). Once the
  // items list contains that asset, we switch to Uploads, scroll it
  // into view, and pulse the card for ~2.4s so producers can find their
  // voice track instantly.
  useEffect(() => {
    if (!highlightAssetId || items.length === 0) return;
    // Voice assets get an "sfx_" or "vt_" or "ps_imp_" prefix — try
    // matching on both raw id and prefixed id.
    const match = items.find((a) =>
      a.id === highlightAssetId
      || a.id === `sfx_${highlightAssetId}`
      || (a.asset_id === highlightAssetId)
      || a.id?.endsWith(highlightAssetId),
    );
    if (!match) return;
    setFilter("Uploads");
    setPulseId(match.id);
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="ps-asset-${match.id}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    const clear = setTimeout(() => setPulseId(null), 2400);
    return () => { clearTimeout(t); clearTimeout(clear); };
  }, [highlightAssetId, items]);

  // Update #156 — refetch when the Music Import modal reports a
  // successful upload OR when the user deletes an import, so the
  // "Uploads" tab and its count badge stay in lock-step with reality.
  useEffect(() => {
    const refetch = async () => {
      try {
        const r = await api.get("/production/assets");
        const real = r.data?.items || [];
        setItems([...real, ...SEED_ASSETS]);
      } catch { /* silent */ }
    };
    window.addEventListener("bfa:library-refresh", refetch);
    return () => window.removeEventListener("bfa:library-refresh", refetch);
  }, []);

  // Live-refresh the recent-SFX list whenever ProductionSuite fires the
  // "bfa:sfx-recent-updated" event on drop. Keeps the chip's counter in
  // sync without polling.
  useEffect(() => {
    const refresh = async () => {
      try {
        const r = await api.get("/production/sfx/recent");
        setSfxRecentIds(r.data?.item_ids || []);
      } catch { /* silent */ }
    };
    window.addEventListener("bfa:sfx-recent-updated", refresh);
    return () => window.removeEventListener("bfa:sfx-recent-updated", refresh);
  }, []);

  // Reset category chip whenever the user leaves the SFX filter — chips are
  // only meaningful inside the SFX tab, so they shouldn't cross-filter.
  useEffect(() => {
    if (filter !== "SFX") {
      if (category) setCategory(null);
      if (showRecentOnly) setShowRecentOnly(false);
    }
  }, [filter, category, showRecentOnly]);

  const filtered = useMemo(() => {
    const wanted = FILTER_TO_TYPE[filter];
    const q = query.trim().toLowerCase();
    // "Recent only" view returns the recent SFX in most-recent-first order,
    // dropping any that are no longer in the manifest.
    if (filter === "SFX" && showRecentOnly) {
      const byId = new Map(
        items.filter((a) => a.source === "sfx_pack").map((a) => [a.id, a]),
      );
      return sfxRecentIds
        .map((iid) => byId.get(`sfx_${iid}`))
        .filter(Boolean)
        .filter((a) => {
          if (!q) return true;
          const hay = `${a.name || ""} ${a.category || ""}`.toLowerCase();
          return hay.includes(q);
        });
    }
    // "Uploads" — the user's own imports. Sorted newest-first so the
    // last thing they dropped in is at the top of the grid.
    if (filter === "Uploads") {
      const uploads = items.filter(isUploadedAsset);
      uploads.sort((a, b) => {
        const at = a.created_at || a.uploaded_at || "";
        const bt = b.created_at || b.uploaded_at || "";
        return bt.localeCompare(at);
      });
      return uploads.filter((a) => {
        if (!q) return true;
        const hay = `${a.name || ""} ${a.type || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return items.filter((a) => {
      if (wanted && a.type !== wanted) return false;
      if (filter === "SFX" && category && a.category !== category) return false;
      if (!q) return true;
      const hay = `${a.name || ""} ${a.type || ""} ${a.category || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, filter, query, category, showRecentOnly, sfxRecentIds]);

  // Live count of uploaded assets — powers the badge on the Uploads pill.
  const uploadCount = useMemo(
    () => items.filter(isUploadedAsset).length,
    [items],
  );

  const handleDragStart = (e, asset) => {
    e.dataTransfer.setData("assetId", asset.id);
    e.dataTransfer.setData("assetData", JSON.stringify(asset));
    e.dataTransfer.effectAllowed = "copy";
    onDragStartAsset?.(asset);
  };

  // Update #151 — delete a user-imported asset (both DB rows + storage).
  const deleteImport = async (asset) => {
    if (!await appConfirm(`Delete "${asset.name}" from your library? This can't be undone.`)) return;
    // Stop any preview of THIS card so its <audio> doesn't hit a 404
    // after the delete completes.
    if (previewId === asset.id) stopPreview();
    try {
      const rawId = asset.id.startsWith("sfx_") ? asset.id.slice(4) : asset.id;
      await api.delete(`/production/import/${rawId}`);
      // Update the in-memory list immediately — cheaper than a re-fetch
      // and keeps the search / category counts in lock-step.
      setItems((prev) => prev.filter((a) => a.id !== asset.id));
      // Fire the same refresh beacon so any other library instance
      // (e.g. the one in a modal) stays in sync too.
      try { window.dispatchEvent(new CustomEvent("bfa:library-refresh")); } catch { /* noop */ }
      toast.success(`Deleted "${asset.name}"`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Delete failed"));
    }
  };

  // Update #156 — persist a renamed asset. Called by the double-click
  // inline editor (and by the pencil button, which just opens the editor).
  // Returns true on success so the caller knows when to exit edit mode.
  const persistRename = async (asset, nextName) => {
    const next = (nextName || "").trim();
    if (!next) { toast.error("Name cannot be empty"); return false; }
    if (next === (asset.name || "")) return true;   // no-op, but treat as success
    try {
      const rawId = asset.id.startsWith("sfx_") ? asset.id.slice(4) : asset.id;
      await api.patch(`/production/import/${rawId}`, { name: next });
      setItems((prev) => prev.map((a) => a.id === asset.id
        ? { ...a, name: next, label: next }
        : a));
      try { window.dispatchEvent(new CustomEvent("bfa:library-refresh")); } catch { /* noop */ }
      toast.success(`Renamed to "${next}"`);
      return true;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Rename failed"));
      return false;
    }
  };

  const togglePreview = (asset) => {
    // Toggling the same card → stop current preview.
    if (previewId === asset.id) { stopPreview(); return; }
    // Switching to a different asset → tear down the old one first.
    stopPreview();
    setPreviewId(asset.id);
    const startPreviewingBar = (durationMs) => {
      const eventId = `lib_${asset.id}`;
      previewEventIdRef.current = eventId;
      try {
        window.dispatchEvent(new CustomEvent("bfa:preview-started", {
          detail: {
            id: eventId,
            label: asset.name || "Library preview",
            source: asset.type || "library",
            color: asset.color || "#4a9eff",
            durationMs,
          },
        }));
      } catch { /* noop */ }
    };
    if (asset.audio_url) {
      // NOTE: no `audio.crossOrigin = "anonymous"` — the audio URL is
      // same-origin (`/api/production/import/...` on the same host), so
      // CORS doesn't apply. Setting `anonymous` would strip the session
      // cookie in some Chromium builds and cause auth-related 401s.
      const audio = new Audio(asset.audio_url);
      audioRef.current = audio;
      audio.onended = () => stopPreview();
      audio.onerror = () => { toast.error("Preview unavailable"); stopPreview(); };
      audio.play().catch((e) => {
        // Ignore "interrupted by pause()" — the user hit Pause, that's
        // the intended flow, not an error worth showing.
        const name = (e && e.name) || "";
        const msg  = (e && e.message) || "";
        if (name === "AbortError" || /interrupted/i.test(msg)) return;
        toast.error("Could not play preview — audio URL may be blocked");
        stopPreview();
      });
      startPreviewingBar(8000);
      // Auto-stop after 8s
      stopTimerRef.current = setTimeout(() => stopPreview(), 8000);
    } else {
      // Synthesised preview so seed assets without a real audio_url are still
      // audible. Store the returned stop() so we can abort mid-flight when
      // the user clicks Pause — otherwise the WebAudio oscillators would
      // play to their scheduled natural end regardless of our other cleanup.
      synthStopRef.current = synthPreview(asset.type);
      startPreviewingBar(1900);
      stopTimerRef.current = setTimeout(() => stopPreview(), 1900);
    }
  };

  return (
    <div className={s.library} data-testid="ps-library">
      <div className={s.libraryHeader}>
        <div className={s.libraryTitle}>// ASSET LIBRARY</div>
        <div className={s.libraryActionBtns}>
          <button type="button" className={`${s.libraryActionBtn} ${s.libraryActionBtnPrimary}`}
            onClick={() => onOpenImport?.("upload")}
            data-testid="ps-library-import">
            <Upload size={10} /> Import music
          </button>
          <button type="button" className={s.libraryActionBtn}
            onClick={() => onOpenImport?.("library")}
            data-testid="ps-library-browse">
            <LibraryIcon size={10} /> Browse imaging
          </button>
          <button type="button" className={s.libraryActionBtn}
            onClick={() => setSfxOpen(true)}
            data-testid="ps-library-sfx-pack">
            <Music4 size={10} /> SFX pack
          </button>
          <button type="button" className={s.libraryActionBtn}
            onClick={() => onOpenImport?.("ai")}
            data-testid="ps-library-ai">
            <Sparkles size={10} /> AI generate
          </button>
        </div>
        <div className={s.filterPills}>
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`${s.snapPill} ${filter === f ? s.snapPillActive : ""}`}
              data-testid={`ps-library-filter-${f.toLowerCase().replace(" ", "-")}`}
            >
              {f}
              {f === "Uploads" && uploadCount > 0 && (
                <span className={s.filterPillBadge} data-testid="ps-library-filter-uploads-count">
                  {uploadCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className={s.librarySearch}>
          <Search size={10} className={s.librarySearchIcon} aria-hidden />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search assets…"
            className={s.librarySearchInput}
            data-testid="ps-library-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className={s.librarySearchClear}
              data-testid="ps-library-search-clear"
              aria-label="Clear search"
            >
              <X size={10} />
            </button>
          )}
          <span className={s.librarySearchCount} data-testid="ps-library-count">
            {filtered.length}
          </span>
        </div>
      </div>
      {filter === "SFX" && sfxCategories.length > 0 && (
        <div className={s.categoryRow} data-testid="ps-library-sfx-categories">
          {sfxRecentIds.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setShowRecentOnly((v) => !v);
                if (!showRecentOnly) setCategory(null);
              }}
              className={`${s.categoryChip} ${s.categoryChipRecent} ${showRecentOnly ? s.categoryChipActive : ""}`}
              data-testid="ps-library-sfx-cat-recent"
              title="Recently used SFX"
            >
              ★ Recent <span className={s.categoryChipCount}>{sfxRecentIds.length}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => { setCategory(null); setShowRecentOnly(false); }}
            className={`${s.categoryChip} ${(category === null && !showRecentOnly) ? s.categoryChipActive : ""}`}
            data-testid="ps-library-sfx-cat-all"
          >
            All <span className={s.categoryChipCount}>{items.filter((a) => a.type === "SFX").length}</span>
          </button>
          {sfxCategories.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => {
                setShowRecentOnly(false);
                setCategory(category === c.slug ? null : c.slug);
              }}
              className={`${s.categoryChip} ${(category === c.slug && !showRecentOnly) ? s.categoryChipActive : ""}`}
              data-testid={`ps-library-sfx-cat-${c.slug}`}
              title={c.label}
            >
              {c.label} <span className={s.categoryChipCount}>{c.count}</span>
            </button>
          ))}
        </div>
      )}
      <div className={s.libraryGrid}>
        {filtered.length === 0 ? (
          filter === "Uploads" ? (
            <div className={s.uploadsEmpty} data-testid="ps-library-uploads-empty">
              <div className={s.uploadsEmptyTitle}>
                <Upload size={12} /> No uploaded music yet
              </div>
              <div className={s.uploadsEmptyBody}>
                Everything you import via <strong>Import music</strong> lands here — safe from the stock imaging &amp; SFX pack.
              </div>
              <button
                type="button"
                className={`${s.libraryActionBtn} ${s.libraryActionBtnPrimary}`}
                onClick={() => onOpenImport?.("upload")}
                data-testid="ps-library-uploads-cta"
              >
                <Upload size={10} /> Upload your first track
              </button>
            </div>
          ) : (
            <div style={{ gridColumn: "span 4", textAlign: "center", padding: 16, color: "#4a5068", fontSize: 11 }}>
              No assets matching &ldquo;{filter}&rdquo;.
            </div>
          )
        ) : filtered.map((a) => {
          // Update #151 — only user-imported assets (source="imaging" +
          // has an /api/production/import/… URL) are deletable. SFX
          // pack items, seed data, and avatar renders are shared /
          // system-owned so they don't show the trash button.
          const canDelete = a.source === "imaging"
            && typeof a.audio_url === "string"
            && a.audio_url.startsWith("/api/production/import/");
          const isEditing = editingId === a.id;
          const isPulsing = pulseId === a.id;
          return (
          <div
            key={`${a.source || "seed"}-${a.id}`}
            draggable={!isEditing}
            onDragStart={(e) => handleDragStart(e, a)}
            className={`${s.assetCard} ${isEditing ? s.assetCardEditing : ""} ${isPulsing ? s.assetCardPulse : ""}`}
            data-testid={`ps-asset-${a.id}`}
          >
            <div className={s.assetDot} style={{ background: a.color }} />
            <div className={s.assetBody}>
              {isEditing ? (
                <AssetNameEditor
                  asset={a}
                  onCommit={async (v) => {
                    const ok = await persistRename(a, v);
                    if (ok) setEditingId(null);
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div
                  className={s.assetName}
                  onDoubleClick={canDelete ? (e) => { e.stopPropagation(); setEditingId(a.id); } : undefined}
                  title={canDelete ? "Double-click to rename" : a.name}
                  data-testid={`ps-asset-name-${a.id}`}
                >
                  {a.name}
                </div>
              )}
              <div className={s.assetMeta}>{a.type} · {a.duration || "0:00"}</div>
            </div>
            {canDelete && !isEditing && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEditingId(a.id); }}
                  className={s.assetRename}
                  data-testid={`ps-asset-rename-${a.id}`}
                  title="Rename this upload"
                  aria-label={`Rename ${a.name}`}
                >
                  <Pencil size={9} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); deleteImport(a); }}
                  className={s.assetDelete}
                  data-testid={`ps-asset-delete-${a.id}`}
                  title="Delete this import"
                  aria-label={`Delete ${a.name}`}
                >
                  <Trash2 size={9} />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => togglePreview(a)}
              className={s.assetPlay}
              data-testid={`ps-asset-preview-${a.id}`}
              title={a.audio_url ? "Preview" : "No audio URL"}
            >
              {previewId === a.id ? <Pause size={10} /> : <Play size={10} />}
            </button>
          </div>
        );})}
      </div>

      {/* SFX Pack modal — Update #120 */}
      {sfxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSfxOpen(false)}
          data-testid="ps-sfx-modal-overlay"
        >
          <div
            className="relative bg-bg-surface border border-line rounded-2xl shadow-2xl max-w-5xl w-full max-h-[85vh] overflow-y-auto p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="ps-sfx-modal"
          >
            <button
              type="button"
              onClick={() => setSfxOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-md text-ink-muted hover:text-ink-primary hover:bg-bg-elevated transition"
              data-testid="ps-sfx-modal-close"
              aria-label="Close SFX pack"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="pr-8 mb-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-amber mb-1">
                // STARTER FX PACK
              </div>
              <h2 className="font-display text-xl font-bold text-ink-primary">Drop-in sound effects</h2>
              <p className="text-ink-secondary text-sm mt-1">
                Import any effect into your imaging library — then drag from the Asset Library into a track.
              </p>
            </div>
            <SfxBrowser onImport={() => { /* PS reads its assets via a different pipe */ }} />
          </div>
        </div>
      )}
    </div>
  );
}

// Update #156 — Inline name editor. Renders a tiny input in place of the
// asset name text so producers can double-click any upload card and
// retype the label without leaving the timeline. Enter commits, Escape
// aborts, click-outside (blur) commits too so the user can just move
// on and their edit sticks.
function AssetNameEditor({ asset, onCommit, onCancel }) {
  const [value, setValue] = React.useState(asset.name || "");
  const [saving, setSaving] = React.useState(false);
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    // Select-all so producers can start typing to replace the label
    // immediately without an intermediate Cmd-A press.
    try { el.setSelectionRange(0, el.value.length); } catch { /* noop */ }
  }, []);
  const commit = async () => {
    if (saving) return;
    setSaving(true);
    await onCommit(value);
    setSaving(false);
  };
  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      disabled={saving}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
      onBlur={commit}
      // Don't let a click inside the editor toggle preview or start a
      // drag on the parent card.
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      className={s.assetNameInput}
      maxLength={140}
      data-testid={`ps-asset-name-input-${asset.id}`}
      aria-label="Rename upload"
    />
  );
}
